import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const EmployeeImprovementTracker = ({ data, metrics }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevation-3">
          <p className="text-sm font-semibold text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-xs text-muted-foreground">
              {entry?.name}: {entry?.value}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-elevation-2 border border-border">
      <div className="flex items-start justify-between mb-4 md:mb-6">
        <div>
          <h3 className="text-base md:text-lg lg:text-xl font-semibold text-foreground mb-1 md:mb-2">
            Employee Improvement Tracking
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Progress metrics with engagement analytics
          </p>
        </div>
        <div className="p-2 md:p-3 rounded-lg bg-success/10">
          <Icon name="TrendingUp" size={20} className="text-success" />
        </div>
      </div>
      <div className="w-full h-64 md:h-72 lg:h-80 mb-4 md:mb-6" aria-label="Employee Improvement Line Chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="month" 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '12px' }}
              iconType="circle"
            />
            <Line 
              type="monotone" 
              dataKey="improvement" 
              stroke="var(--color-success)" 
              strokeWidth={2}
              name="Improvement Rate"
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="engagement" 
              stroke="var(--color-primary)" 
              strokeWidth={2}
              name="Engagement Rate"
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {metrics?.map((metric, index) => (
          <div key={index} className="bg-muted rounded-lg p-3 md:p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name={metric?.icon} size={16} className="text-primary" />
              <p className="text-xs md:text-sm text-muted-foreground">{metric?.label}</p>
            </div>
            <p className="text-lg md:text-xl lg:text-2xl font-bold text-foreground">{metric?.value}</p>
            <div className="flex items-center gap-1 mt-1">
              <Icon 
                name={metric?.trend > 0 ? 'ArrowUp' : 'ArrowDown'} 
                size={14} 
                className={metric?.trend > 0 ? 'text-success' : 'text-error'}
              />
              <span className={`text-xs font-medium ${metric?.trend > 0 ? 'text-success' : 'text-error'}`}>
                {Math.abs(metric?.trend)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmployeeImprovementTracker;